import { toast as sonnerToast } from "sonner";

/**
 * Toast utility hook for displaying notifications
 * 
 * Provides a consistent interface for showing success, error, warning, and info toasts
 * throughout the application.
 * 
 * @example
 * ```tsx
 * const toast = useToast();
 * 
 * // Success toast
 * toast.success("Account created successfully!");
 * 
 * // Error toast
 * toast.error("Failed to create account");
 * 
 * // With description
 * toast.error("Failed to create account", {
 *   description: "Please check your email and try again"
 * });
 * ```
 */
export const useToast = () => {
  return {
    /**
     * Display a success toast
     */
    success: (message: string, options?: { description?: string; duration?: number }) => {
      sonnerToast.success(message, {
        description: options?.description,
        duration: options?.duration || 4000,
      });
    },

    /**
     * Display an error toast
     */
    error: (message: string, options?: { description?: string; duration?: number }) => {
      sonnerToast.error(message, {
        description: options?.description,
        duration: options?.duration || 5000,
      });
    },

    /**
     * Display a warning toast
     */
    warning: (message: string, options?: { description?: string; duration?: number }) => {
      sonnerToast.warning(message, {
        description: options?.description,
        duration: options?.duration || 4000,
      });
    },

    /**
     * Display an info toast
     */
    info: (message: string, options?: { description?: string; duration?: number }) => {
      sonnerToast.info(message, {
        description: options?.description,
        duration: options?.duration || 4000,
      });
    },

    /**
     * Display a loading toast
     */
    loading: (message: string) => {
      return sonnerToast.loading(message);
    },

    /**
     * Display a promise toast (shows loading, then success/error based on promise result)
     */
    promise: <T,>(
      promise: Promise<T>,
      options: {
        loading: string;
        success: string | ((data: T) => string);
        error: string | ((error: Error) => string);
      }
    ) => {
      return sonnerToast.promise(promise, options);
    },

    /**
     * Dismiss a specific toast or all toasts
     */
    dismiss: (toastId?: string | number) => {
      sonnerToast.dismiss(toastId);
    },
  };
};

/**
 * Direct toast functions for use outside of React components
 */
export const toast = {
  success: (message: string, options?: { description?: string; duration?: number }) => {
    sonnerToast.success(message, {
      description: options?.description,
      duration: options?.duration || 4000,
    });
  },

  error: (message: string, options?: { description?: string; duration?: number }) => {
    sonnerToast.error(message, {
      description: options?.description,
      duration: options?.duration || 5000,
    });
  },

  warning: (message: string, options?: { description?: string; duration?: number }) => {
    sonnerToast.warning(message, {
      description: options?.description,
      duration: options?.duration || 4000,
    });
  },

  info: (message: string, options?: { description?: string; duration?: number }) => {
    sonnerToast.info(message, {
      description: options?.description,
      duration: options?.duration || 4000,
    });
  },

  loading: (message: string) => {
    return sonnerToast.loading(message);
  },

  promise: <T,>(
    promise: Promise<T>,
    options: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: Error) => string);
    }
  ) => {
    return sonnerToast.promise(promise, options);
  },

  dismiss: (toastId?: string | number) => {
    sonnerToast.dismiss(toastId);
  },
};